import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import { useState } from "react"

import { useCustomerProductAssociationColumns } from "../hooks/useCustomerProductAssociationColumns"
import type { CompanyProductAssociationListItem } from "../hooks/useGetCompanyProductAssociations"

import { ProductAlternativesPanel } from "./ProductAlternatives/ProductAlternativesPanel"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"

interface CustomerProductAssociationsTableProps {
  items: CompanyProductAssociationListItem[]
  isFetching: boolean
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  tableContainerRef: RefObject<HTMLDivElement>
  onScroll: () => void
  onEdit: (item: CompanyProductAssociationListItem) => void
  onRemove: (item: CompanyProductAssociationListItem) => Promise<boolean>
  canEdit: boolean
}

export const CustomerProductAssociationsTable: FC<CustomerProductAssociationsTableProps> = ({
  items,
  isFetching,
  sorting,
  onSortingChange,
  tableContainerRef,
  onScroll,
  onEdit,
  onRemove,
  canEdit,
}) => {
  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})
  const columns = useCustomerProductAssociationColumns({ onEdit, onRemove, canEdit })

  return (
    <AerosBaseTable
      features={{
        tanstackOptions: {
          data: items,
          columns,
          state: { isLoading: isFetching, sorting, expanded: expandedRow },
          onSortingChange,
          onExpandedChange: setExpandedRow,
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getRowId: (row) =>
            `${row.companyProductAssociationId}-${row.companyProductAssociationLocationId ?? "no-location"}`,
          getRowCanExpand: (row) => row.original.companyProductAssociationLocationId !== null,
          renderExpandedContent: (row) => (
            <ProductAlternativesPanel
              companyProductAssociationLocationId={row.original.companyProductAssociationLocationId!}
              shipToLocation={row.original.shipToLocation}
              productPublicId={row.original.product?.publicId ?? undefined}
            />
          ),
        },
      }}
      slotProps={{
        container: {
          className: "h-full",
          ref: tableContainerRef,
          onScroll,
        },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      noDataMessage='No associated products found'
    />
  )
}
